import {
  AdminSpinner,
  Button,
  Div,
  H3,
  H5,
  Img,
  Modal,
  P,
  PropsModal,
  ProviderLogoWrapper,
  SuccessToast,
  TextButton,
  disconnectLocationWithPos,
  getPosIntegrationLink,
  isAdmin,
  lightTheme,
  renderPosImage,
  reportError,
  useIsIOSSafari,
  useSafeWindowOpen,
} from '@vgrubs/components';
import { PosType } from 'components/src/types/api/location.types';
import { useState } from 'react';

export const PosDetailsModal = ({
  modalIsOpen,
  closeModal,
  id,
  provider,
  pathPrefix,
  getProvidersStatuses,
  onOpenPlaybook,
}: PropsModal & {
  modalIsOpen: boolean;
  provider: PosType;
  id: string | number;
  pathPrefix: 'locations' | 'brands';
  getProvidersStatuses: (silent?: boolean) => void;
  /** Swaps this modal for the integration playbook. */
  onOpenPlaybook?: () => void;
}) => {
  const [loading, setLoading] = useState(false);
  const [disconnectModal, setDisconnectModal] = useState(false);
  // iOS Safari blocks window.open after an await, so the link waits for a second tap
  const [integrationUrl, setIntegrationUrl] = useState<string | null>(null);
  const openSafe = useSafeWindowOpen();
  const isIOSSafari = useIsIOSSafari();

  // The endpoint is admin/support only and takes a location id
  const canOpenIntegrationPage = isAdmin && pathPrefix === 'locations';

  const handleClose = () => {
    setIntegrationUrl(null);
    closeModal?.();
  };

  const openIntegrationPage = (url: string) => {
    openSafe(url, { immediate: true, target: '_blank' });
    setIntegrationUrl(null);
  };

  async function handleIntegrationPage() {
    try {
      setLoading(true);
      const res = await getPosIntegrationLink(id, provider);
      const url = res.data.data;

      if (isIOSSafari) {
        setIntegrationUrl(url);
      } else {
        openIntegrationPage(url);
      }
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  }

  async function handleDisconnect(switchToTabletMode: boolean) {
    setDisconnectModal?.(false);
    try {
      setLoading(true);
      const res = await disconnectLocationWithPos(
        id,
        provider,
        switchToTabletMode
      );
      await getProvidersStatuses(true);
      SuccessToast(res.data.message);
      closeModal?.();
      setLoading(false);
    } catch (error) {
      reportError(error);
      setLoading(false);
    }
  }

  return (
    <>
      {loading && <AdminSpinner />}

      <Modal
        isOpen={modalIsOpen}
        closeModal={handleClose}
        logo={false}
        header="Provider Details"
      >
        <Div $flex $center>
          <ProviderLogoWrapper>
            <span>
              <Img alt="" src={renderPosImage(provider)} />
            </span>
          </ProviderLogoWrapper>
        </Div>

        <H5 $mb="24">
          The provider is connected. You may disconnect it if it’s no longer
          needed.
        </H5>

        <Div $flex $column $gap="8px">
          {onOpenPlaybook && (
            <TextButton
              $underline
              color={lightTheme.primary}
              style={{ justifyContent: 'center' }}
              onClick={() => {
                handleClose();
                onOpenPlaybook();
              }}
            >
              Integration playbook
            </TextButton>
          )}
          {canOpenIntegrationPage &&
            (integrationUrl ? (
              <Button
                $selected
                $w="100%"
                onClick={() => openIntegrationPage(integrationUrl)}
              >
                Proceed to integration page
              </Button>
            ) : (
              <Button $selected $w="100%" onClick={handleIntegrationPage}>
                Integration page
              </Button>
            ))}
          <Button
            $selected
            $gray
            $w="100%"
            onClick={() => setDisconnectModal(true)}
          >
            Disconnect
          </Button>
        </Div>
      </Modal>

      <Modal
        isOpen={!!disconnectModal}
        logo={false}
        exitButton={false}
        zIndex={111}
        width="400px"
      >
        <H3 $mb="8" $mt="32">
          Are you sure?
        </H3>
        <P $mb="50">Are you sure you want to disconnect this POS?</P>

        <Div $flex $gap="8px" $column $mt="32">
          <Button $selected $w="100%" onClick={() => handleDisconnect(true)}>
            Disconnect and switch to tablet mode
          </Button>
          <Button $selected $w="100%" onClick={() => handleDisconnect(false)}>
            Disconnect and keep POS mode
          </Button>
          <Button
            $selected
            $gray
            $w="100%"
            onClick={() => setDisconnectModal(false)}
          >
            Don't disconnect
          </Button>
        </Div>
      </Modal>
    </>
  );
};
